import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Table } from "../Table/Table";
import {
  fetchSocialMediaSubmission,
  updateApprovalStatus,
  getContentById,
} from "../../services/firebase";
import { formatToET } from "../utils/formatToET";
import { UserInspiration } from "../../types/userInspirations";
import { Column } from "../../types/table";
import { serverTimestamp } from "firebase/firestore";
import {
  FaInstagram,
  FaTiktok,
  FaDiscord as FaDiscordAlt,
  FaXTwitter,
  FaFacebook,
  FaYoutube
} from "react-icons/fa6";

const Spinner = React.memo(() => (
  <span className="ml-2 w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
));

const UserInspirations = (): JSX.Element => {
  const [data, setData] = useState<UserInspiration[]>([]);
  const [loading, setLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState<{
    [key: string]: "approve" | "reject" | null;
  }>({});

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const handleApprovalToggle = useCallback(
    async (row: Record<string, any>, approve?: boolean) => {
      const id = row.id;
      if (!id || approve === undefined) return;
      setButtonLoading((prev) => ({
        ...prev,
        [id]: approve ? "approve" : "reject",
      }));

      try {
        await updateApprovalStatus(row, approve);
        setData((prev) =>
          prev.map((entry) =>
            entry.id === id
              ? {
                  ...entry,
                  isApproved: approve,
                  statusUpdatedAt: serverTimestamp(),
                }
              : entry
          )
        );
      } catch (error) {
        console.error("Failed to update approval status", error);
      } finally {
        setButtonLoading((prev) => ({ ...prev, [id]: null }));
      }
    },
    []
  );

  const toggleDropdown = useCallback((id?: string) => {
    if (!id) return;
    setOpenDropdownId((prev) => (prev === id ? null : id));
  }, []);

  const columns: Column<UserInspiration>[] = useMemo(
    () => [
      { header: "Name", accessor: "firstName" },
      { header: "Email", accessor: "email" },
      {
        header: "Content Title",
        accessor: "contentTitle",
        render: (url: string, data: UserInspiration) => (
          <a
            href={data.contentType == 'draft-ideas' ? `/draft-ideas/${data.slug}` : `/punishments/${data.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {url}
          </a>
        ),
      },
      {
        header: "Content Type",
        accessor: "contentType",
      },
      {
        header: "Platform Type",
        accessor: "type",
        render: (_, data: UserInspiration) => {
          const platform_type = data.type;
          const facebook = platform_type === "facebook" && <FaFacebook />;
          const instagram = platform_type === "instagram" && <FaInstagram />;
          const twitter = (platform_type === "twitter" || platform_type === 'x') && <FaXTwitter />;
          const tiktok = platform_type === "tiktok" && <FaTiktok />;
          const youtube = platform_type === "youtube" && <FaYoutube />;

          return facebook || instagram || twitter || tiktok || youtube;
        },
      },
      {
        header: "Social Media Link",
        accessor: "url",
        render: (url: string) => (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {url}
          </a>
        ),
      },
      {
        header: "Submitted At",
        accessor: "submittedAt",
        render: (value: Date) => (
          <div className="whitespace-nowrap">{formatToET(new Date(value))}</div>
        ),
      },
      {
        header: "Status",
        accessor: "isApproved",
        render: (_value?: boolean, data?: UserInspiration) => {
          if (!data?.statusUpdatedAt) {
            return <span className="text-gray-500">Pending</span>;
          }

          if (data.isApproved === true) {
            return <span className="text-green-600 font-medium">Approved</span>;
          }

          if (data.isApproved === false) {
            return <span className="text-red-600 font-medium">Rejected</span>;
          }
        },
      },
      {
        header: "",
        accessor: "id",
        render: (_id, row) => {
          const loadingState = buttonLoading[row.id ?? ""];
          const isOpen = openDropdownId === row.id;

          return (
            <div className="relative inline-block text-left">
              <button
                onClick={() => toggleDropdown(row.id)}
                className="px-2 py-1 text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                aria-haspopup="true"
                aria-expanded={isOpen}
              >
                ...
              </button>

              {isOpen && (
                <div
                  ref={dropdownRef}
                  className="absolute right-0 z-10 mt-2 w-32 origin-top-right bg-white border border-gray-200 divide-y divide-gray-100 rounded-md shadow-lg"
                >
                  <button
                    onClick={() => handleApprovalToggle(row, true)}
                    disabled={
                      loadingState === "approve" ||
                      (row.statusUpdatedAt && row.isApproved === true)
                    }
                    className="flex items-center w-full px-4 py-2 text-sm text-left text-green-600 hover:bg-gray-100 disabled:opacity-50"
                  >
                    Approve
                    {loadingState === "approve" && <Spinner />}
                  </button>
                  <button
                    onClick={() => handleApprovalToggle(row, false)}
                    disabled={
                      loadingState === "reject" ||
                      (row.statusUpdatedAt && row.isApproved === false)
                    }
                    className="flex items-center w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-gray-100 disabled:opacity-50"
                  >
                    Reject
                    {loadingState === "reject" && <Spinner />}
                  </button>
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [buttonLoading, openDropdownId, handleApprovalToggle, toggleDropdown]
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        const entries = await fetchSocialMediaSubmission();
        const transformed = await Promise.all(
          entries.map(async (entry) => {
            const content = await getContentById(entry.contentId, entry.contentType === 'draft-ideas' ? 'draftIdeas' : 'content');
            const slug = content?.slug;
            return {
              ...entry,
              slug,
            };
          })
        );
        setData(transformed);
      } catch (error) {
        console.error("Failed to load social media data.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdownId]);

  if (loading) return <div>Loading...</div>;
  return <Table columns={columns} data={data} />;
};

export default UserInspirations;
